import { useCallback, useEffect, useMemo, useState } from "react";
import { LocalStorageFilterTemplateRepository } from "../repositories/LocalStorageFilterTemplateRepository";
import {
  ExtractSavableFiltersInput,
  FILTER_TEMPLATE_NAME_MAX_LENGTH,
  FilterTemplate,
  SavedFilterValues,
} from "../types/FilterTemplateTypes";
import { extractSavableFilters } from "../utils/filterTemplateUtils";
import { useStorageUsername } from "./useStorageUsername";

const createTemplateId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `template-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const validateTemplateName = (name: string): string => {
  const trimmedName = name.trim();
  if (!trimmedName) {
    throw new Error("Template name is required.");
  }

  if (trimmedName.length > FILTER_TEMPLATE_NAME_MAX_LENGTH) {
    throw new Error(
      `Template name must be ${FILTER_TEMPLATE_NAME_MAX_LENGTH} characters or fewer.`,
    );
  }

  return trimmedName;
};

export const useFilterTemplates = () => {
  const userName = useStorageUsername();
  const repository = useMemo(
    () => new LocalStorageFilterTemplateRepository(userName),
    [userName],
  );

  const [templates, setTemplates] = useState<FilterTemplate[]>([]);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refreshTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      const nextTemplates = await repository.getAll();
      setTemplates(nextTemplates);
    } finally {
      setIsLoading(false);
    }
  }, [repository]);

  useEffect(() => {
    if (!userName) {
      setTemplates([]);
      setActiveTemplateId(null);
      return;
    }

    void refreshTemplates();
  }, [refreshTemplates, userName]);

  const assertUserSession = useCallback(() => {
    if (!userName.trim()) {
      throw new Error("Unable to save filter templates until you are signed in.");
    }
  }, [userName]);

  const saveTemplate = useCallback(
    async (name: string, input: ExtractSavableFiltersInput) => {
      assertUserSession();
      const trimmedName = validateTemplateName(name);

      const duplicate = templates.find(
        (template) =>
          template.name.toLowerCase() === trimmedName.toLowerCase(),
      );
      if (duplicate) {
        throw new Error("A template with this name already exists.");
      }

      const now = new Date().toISOString();
      const template: FilterTemplate = {
        id: createTemplateId(),
        name: trimmedName,
        createdAt: now,
        updatedAt: now,
        filters: extractSavableFilters(input),
      };

      await repository.save(template);
      await refreshTemplates();
      setActiveTemplateId(template.id);
      return template;
    },
    [assertUserSession, refreshTemplates, repository, templates],
  );

  const updateTemplate = useCallback(
    async (id: string, input: ExtractSavableFiltersInput) => {
      assertUserSession();
      const existing = templates.find((template) => template.id === id);
      if (!existing) {
        throw new Error("Filter template not found.");
      }

      const updatedTemplate: FilterTemplate = {
        ...existing,
        updatedAt: new Date().toISOString(),
        filters: extractSavableFilters(input),
      };

      await repository.update(updatedTemplate);
      await refreshTemplates();
      setActiveTemplateId(id);
      return updatedTemplate;
    },
    [assertUserSession, refreshTemplates, repository, templates],
  );

  const renameTemplate = useCallback(
    async (id: string, name: string) => {
      assertUserSession();
      const trimmedName = validateTemplateName(name);

      const existing = templates.find((template) => template.id === id);
      if (!existing) {
        throw new Error("Filter template not found.");
      }

      const duplicate = templates.find(
        (template) =>
          template.id !== id &&
          template.name.toLowerCase() === trimmedName.toLowerCase(),
      );
      if (duplicate) {
        throw new Error("A template with this name already exists.");
      }

      const updatedTemplate: FilterTemplate = {
        ...existing,
        name: trimmedName,
        updatedAt: new Date().toISOString(),
      };

      await repository.update(updatedTemplate);
      await refreshTemplates();
    },
    [assertUserSession, refreshTemplates, repository, templates],
  );

  const deleteTemplate = useCallback(
    async (id: string) => {
      assertUserSession();
      await repository.deleteById(id);
      await refreshTemplates();
      setActiveTemplateId((currentId) => (currentId === id ? null : currentId));
    },
    [assertUserSession, refreshTemplates, repository],
  );

  const loadTemplate = useCallback(
    (id: string): SavedFilterValues => {
      const template = templates.find((item) => item.id === id);
      if (!template) {
        throw new Error("Filter template not found.");
      }

      setActiveTemplateId(id);
      return template.filters;
    },
    [templates],
  );

  const clearActiveTemplate = useCallback(() => {
    setActiveTemplateId(null);
  }, []);

  const activeTemplate = useMemo(
    () => templates.find((template) => template.id === activeTemplateId) ?? null,
    [activeTemplateId, templates],
  );

  return {
    templates,
    activeTemplateId,
    activeTemplate,
    isLoading,
    saveTemplate,
    updateTemplate,
    renameTemplate,
    deleteTemplate,
    loadTemplate,
    clearActiveTemplate,
    refreshTemplates,
  };
};
